/** A walked line: its points, the ground under each, and how far along the line each lies. */
export interface WalkLine {
  readonly xy: Float32Array;
  readonly ground: Float32Array;
  readonly along: Float32Array;
  readonly length: number;
  /** How far from a corner a walker begins to round it, in metres; none keeps the corners. Only
   *  the corners at the points from `first` to `last` are rounded: a doorstep is walked square. */
  readonly corner: number;
  readonly first: number;
  readonly last: number;
  /** Where on the line it was last asked for: the next asking is nearly always just past it. */
  cursor: number;
}

/* A corner sharper than this is turned on the spot: rounded, the walker would turn back short of
   the place the line was laid to. */
const SHARPEST_COS = Math.cos((150 * Math.PI) / 180);

export function lineOf(
  points: readonly (readonly [number, number, number])[],
  corner = 0,
  first = 1,
  last = points.length - 2
): WalkLine {
  const xy = new Float32Array(points.length * 2),
    ground = new Float32Array(points.length),
    along = new Float32Array(points.length);
  points.forEach(([x, y, h], i) => {
    xy[i * 2] = x;
    xy[i * 2 + 1] = y;
    ground[i] = h;
    if (i > 0) along[i] = along[i - 1]! + Math.hypot(x - points[i - 1]![0], y - points[i - 1]![1]);
  });
  return {
    xy,
    ground,
    along,
    length: along[points.length - 1] ?? 0,
    corner,
    first: Math.max(1, first),
    last: Math.min(points.length - 2, last),
    cursor: 0,
  };
}

/** Where one stands a share of the way along a line, and the way it is heading there. */
export interface OnLine {
  x: number;
  y: number;
  ground: number;
  heading: number;
}

/* How far either side of point `v` a walker rounds the corner there: no further than half of
   either span, so two corners never share a stretch of the line. */
function rounding(line: WalkLine, v: number): number {
  if (line.corner <= 0 || v < line.first || v > line.last) return 0;
  const before = line.along[v]! - line.along[v - 1]!,
    after = line.along[v + 1]! - line.along[v]!;
  if (before <= 0 || after <= 0) return 0;
  const [x, y] = [line.xy[v * 2]!, line.xy[v * 2 + 1]!];
  const turn =
    ((x - line.xy[v * 2 - 2]!) * (line.xy[v * 2 + 2]! - x) +
      (y - line.xy[v * 2 - 1]!) * (line.xy[v * 2 + 3]! - y)) /
    (before * after);
  return turn < SHARPEST_COS ? 0 : Math.min(line.corner, before / 2, after / 2);
}

/* The point `u` of the way round the corner at point `v`, begun and ended `reach` from it. */
function rounded(line: WalkLine, v: number, reach: number, u: number, out: OnLine): void {
  const [x, y] = [line.xy[v * 2]!, line.xy[v * 2 + 1]!];
  const before = reach / (line.along[v]! - line.along[v - 1]!),
    after = reach / (line.along[v + 1]! - line.along[v]!);
  const [ax, ay] = [(line.xy[v * 2 - 2]! - x) * before, (line.xy[v * 2 - 1]! - y) * before];
  const [bx, by] = [(line.xy[v * 2 + 2]! - x) * after, (line.xy[v * 2 + 3]! - y) * after];
  const w = 1 - u;
  out.x = x + w * w * ax + u * u * bx;
  out.y = y + w * w * ay + u * u * by;
  out.heading = Math.atan2(u * by - w * ay, u * bx - w * ax);
}

/** Finds the point `share` of the way along a line, into `out`. */
export function alongLine(line: WalkLine, share: number, out: OnLine): OnLine {
  const s = Math.max(0, Math.min(1, share)) * line.length;
  const last = line.along.length - 1;
  let i = Math.min(line.cursor, last - 1);
  while (i > 0 && line.along[i]! > s) i--;
  while (i < last - 1 && line.along[i + 1]! < s) i++;
  line.cursor = i;
  const span = line.along[i + 1]! - line.along[i]!;
  const t = span > 0 ? (s - line.along[i]!) / span : 0;
  const [ax, ay, bx, by] = [
    line.xy[i * 2]!,
    line.xy[i * 2 + 1]!,
    line.xy[i * 2 + 2]!,
    line.xy[i * 2 + 3]!,
  ];
  out.x = ax + (bx - ax) * t;
  out.y = ay + (by - ay) * t;
  out.ground = line.ground[i]! + (line.ground[i + 1]! - line.ground[i]!) * t;
  // A point laid twice has no heading of its own: it has that of the way on from it.
  let h = i;
  while (h < last - 1 && line.along[h + 1]! <= line.along[h]!) h++;
  out.heading = Math.atan2(
    line.xy[h * 2 + 3]! - line.xy[h * 2 + 1]!,
    line.xy[h * 2 + 2]! - line.xy[h * 2]!
  );
  const entering = rounding(line, i),
    leaving = rounding(line, i + 1);
  const from = s - line.along[i]!,
    to = line.along[i + 1]! - s;
  if (from < entering) rounded(line, i, entering, 0.5 + from / (2 * entering), out);
  else if (to < leaving) rounded(line, i + 1, leaving, 0.5 - to / (2 * leaving), out);
  return out;
}
